/**
 * Utility functions for performance optimization
 * 
 * This file contains utilities for monitoring and optimizing 
 * code loading performance.
 */

/**
 * Prefetches a component by executing its lazy loader function
 * 
 * @param {Function} lazyComponent - A React.lazy component reference
 * @returns {Promise} - Promise that resolves when the component is loaded
 */
export const prefetchComponent = (lazyComponent) => {
  if (!lazyComponent) return Promise.resolve();
  
  // For React.lazy components, we need to access the internal properties
  // React.lazy() creates an object with a $$typeof property and a _payload property
  // The _payload has a _status property and a _result property
  // We need to check if the component is a React.lazy component
  if (typeof lazyComponent !== 'function' && 
      lazyComponent._payload && 
      typeof lazyComponent._init === 'function') {
    
    // If already loaded, return resolved promise
    if (lazyComponent._payload._status === 1) {
      return Promise.resolve(lazyComponent._payload._result);
    }
    
    // Otherwise, manually trigger the React.lazy loading mechanism
    // This is a safe way to preload the component without executing it
    try {
      // This forces React to load the component
      lazyComponent._init(lazyComponent._payload);
      return Promise.resolve();
    } catch (e) {
      return Promise.resolve();
    }
  }
  
  // Fallback for regular dynamic imports (not React.lazy components)
  if (typeof lazyComponent === 'function') {
    return lazyComponent().catch(() => {
      // Silently catch errors to not break the application
      return Promise.resolve();
    });
  }
  
  return Promise.resolve();
};

/**
 * Prefetches multiple components in parallel
 * 
 * @param {Array|Object} components - An array or object of lazy components
 * @returns {Promise} - Promise that resolves when all components are loaded
 */
export const prefetchComponents = (components) => {
  if (!components) return Promise.resolve();
  
  // Handle array of components
  if (Array.isArray(components)) {
    return Promise.all(components.map(comp => prefetchComponent(comp)));
  }
  
  // Handle object of components
  if (typeof components === 'object') {
    return Promise.all(Object.values(components).map(comp => prefetchComponent(comp)));
  }
  
  return Promise.resolve();
};

/**
 * Schedule a function to run during browser idle time
 * 
 * @param {Function} callback - The function to execute
 * @param {Object} options - Options object
 * @param {number} options.timeout - Maximum time to wait before forcing execution
 * @param {number} options.delay - Delay before scheduling the idle callback
 * @returns {number|undefined} - The idle callback ID or undefined
 */
export const scheduleIdleTask = (callback, { timeout = 2000, delay = 0 } = {}) => {
  if (typeof callback !== 'function') return;
  
  // Wrap the callback in a try-catch to prevent unhandled errors
  const safeCallback = () => {
    try {
      callback();
    } catch (err) {
      console.warn('Error in idle task:', err);
    }
  };
  
  const scheduleTask = () => {
    if ('requestIdleCallback' in window) {
      return window.requestIdleCallback(safeCallback, { timeout });
    } else {
      return setTimeout(safeCallback, timeout);
    }
  };
  
  if (delay > 0) {
    return setTimeout(scheduleTask, delay);
  } else {
    return scheduleTask();
  }
};

/**
 * Cancel a previously scheduled idle task
 * 
 * @param {number} id - The ID returned from scheduleIdleTask
 */
export const cancelIdleTask = (id) => {
  if (id == null) return;
  
  if ('cancelIdleCallback' in window) {
    window.cancelIdleCallback(id);
  } else {
    clearTimeout(id);
  }
};

/**
 * Records a performance mark and measure for debugging
 * 
 * @param {string} name - The name of the event to record
 * @param {string} type - The type of event (start, end, etc.)
 */
export const recordPerformanceMark = (name, type) => {
  if (!('performance' in window) || !name) return;
  
  try {
    const markName = `${name}:${type}`;
    performance.mark(markName);
    
    if (type === 'end' && performance.getEntriesByName(`${name}:start`).length > 0) {
      performance.measure(name, `${name}:start`, `${name}:end`);
    }
  } catch (err) {
    console.warn('Error recording performance mark:', err);
  }
};